import { gql } from '@apollo/client'
import client from '../utils/client'
import { MutationHandlerError } from '../utils/errors'
import {
	RestaurantMessageAttachment,
	RestaurantMessageContent,
	RestaurantMessageFormat,
} from './createRestaurantMessage'

export type RestaurantMessageSendResult = {
	slug: string | null
	restaurantName: string
	// LetterXpress-Auftrag, EchtPost-Karte oder Empfängeradresse der Mail
	reference: string | null
	error: string | null
}

type SendMessageResult =
	| { success: true; results: RestaurantMessageSendResult[]; errors: null }
	| { success: false; results: undefined; errors: MutationHandlerError }

const SEND_MESSAGE_MUTATION = gql`
	mutation SendMessage(
		$settlementIds: [ID!]!
		$slugs: [String]!
		$sandbox: Boolean!
		$format: RestaurantMessageFormat!
		$email: String
		$intro: String
		$greeting: String
		$content: RestaurantMessageContent
		$subject: String
		$attachments: [RestaurantMessageAttachment!]
	) {
		sendMessage(
			settlementIds: $settlementIds
			slugs: $slugs
			sandbox: $sandbox
			format: $format
			email: $email
			intro: $intro
			greeting: $greeting
			content: $content
			subject: $subject
			attachments: $attachments
		) {
			slug
			restaurantName
			reference
			error
		}
	}
`

// Brief an LetterXpress oder Postkarte an EchtPost, je Slug ein Stück und null an die eigene
// Testadresse; die Mail geht als eine Nachricht an email, mit Slug in dessen Kommunikation. In der
// Sandbox wird nichts gedruckt.
const sendMessage = async ({
	settlementIds,
	slugs,
	sandbox,
	format,
	email,
	intro,
	greeting,
	content,
	subject,
	attachments,
}: {
	settlementIds: string[]
	slugs: (string | null)[]
	sandbox: boolean
	format: RestaurantMessageFormat
	email?: string
	intro?: string
	greeting?: string
	content?: RestaurantMessageContent
	subject?: string
	attachments?: RestaurantMessageAttachment[]
}): Promise<SendMessageResult> => {
	try {
		const { data, errors } = await client.mutate({
			mutation: SEND_MESSAGE_MUTATION,
			variables: {
				settlementIds,
				slugs,
				sandbox,
				format,
				email,
				intro,
				greeting,
				content,
				subject,
				attachments,
			},
			errorPolicy: 'all',
			// Ein Brief, auch aus der Sandbox, erscheint danach gleich als "Brief verschickt" im Dialog und
			// im Log, sein Nachfass-Alarm am Wecker.
			refetchQueries: ['RestaurantCommunications', 'RestaurantAlarms'],
		})

		return errors
			? { success: false, results: undefined, errors }
			: { success: true, results: data.sendMessage, errors: null }
	} catch (e) {
		if (e && e.networkError) return { success: false, results: undefined, errors: 'NETWORK_ERROR' }
		throw e
	}
}

export default sendMessage
